import { useState, useMemo, useCallback, useEffect } from "react";
import { getSortConfigs, tableColumns } from "./PoliciesPageListColumnConfig";
import {
  Loader,
  Switch,
  TableSortable,
  Typography,
} from "@saviynt/design-system";
import {
  PolicyNameCell,
  SeverityCell,
  FindingsCell,
  TagsCell,
} from "./CellRenderer/CellRenderer";
import { EmptyState, TableSkeletonLoader } from "@repo/ui";
import { useIntl } from "react-intl";
import ActionButton from "./ActionButton/ActionButton";
import { useDebounce } from "@repo/mfe-core/host";
import { usePoliciesList } from "../../hooks/usePolicy";
import classNames from "classnames";
import { Policy } from "@repo/api/schemas/policies.schema";
import PoliciesSidepanel from "../PoliciesSidepanel/PoliciesSidepanel";
import "./PoliciesPageList.css";
import { usePoliciesFilter } from "../../context/PoliciesFilterContext";
import useGetUserPermissions, {
  PERMISSION_KEYS,
} from "../../hooks/useUserPermissions";

export default function PoliciesPageList() {
  const { filterValues, isFilterReady } = usePoliciesFilter();
  const intl = useIntl();
  const [columns, setColumns] = useState(tableColumns);
  const { permission } = useGetUserPermissions(
    PERMISSION_KEYS.ISPM_POLICY_MANAGE,
  );
  const [selectedRowIndex, setSelectedRowIndex] = useState<
    number | undefined
  >();
  const [isSidepanelOpen, setIsSidepanelOpen] = useState(false);
  const [bridgePosition, setBridgePosition] = useState({
    top: 0,
    left: 0,
    height: 0,
  });

  const [sortConfig, setSortConfig] = useState<{
    sort?: string;
    order?: string;
  }>({});

  const debouncedFilterValues = useDebounce(filterValues, 500);
  const debouncedSortConfig = useDebounce(sortConfig, 300);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    usePoliciesList(
      { ...debouncedFilterValues, ...debouncedSortConfig },
      { enabled: isFilterReady },
    );

  const tableData = useMemo(
    () => data?.pages.flatMap((page) => page.values) || [],
    [data],
  );

  const systemTags = useMemo(
    () => data?.pages.flatMap((page) => page.tags) || [],
    [data],
  );

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const updateBridgePosition = useCallback((rowIndex: number) => {
    const tableRow = document.querySelector(
      `[data-testid="PoliciesPageList-Table"] tbody tr:nth-child(${rowIndex + 1})`,
    );
    if (tableRow) {
      const rect = tableRow.getBoundingClientRect();
      setBridgePosition({
        top: rect.top + rect.height / 2,
        left: 0,
        height: rect.height,
      });
    }
  }, []);

  const handleRowClick = useCallback(
    (_row: Policy, rowIndex: number) => {
      setSelectedRowIndex(rowIndex);
      setIsSidepanelOpen(true);
      setTimeout(() => updateBridgePosition(rowIndex), 0);
    },
    [updateBridgePosition, tableData],
  );

  const handleColumnSort = useCallback((columnKey: string, order: string) => {
    setSortConfig({ sort: columnKey, order });
  }, []);

  const handleScroll = useCallback(() => {
    if (selectedRowIndex !== undefined) {
      updateBridgePosition(selectedRowIndex);
    }
  }, [selectedRowIndex, updateBridgePosition]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const tableRows = useMemo(
    () =>
      tableData.map((row) => ({
        name: <PolicyNameCell row={row} />,
        severity: <SeverityCell severity={row.severity} />,
        attribution_count: <FindingsCell row={row} />,
        tags: <TagsCell row={row} systemTags={systemTags} />,
        is_active: (
          <Switch
            isChecked={row.is_active}
            ariaLabel={intl.formatMessage({
              defaultMessage: "Toggle button for policy state",
              id: "0R4e0S",
            })}
          />
        ),
        actionColumn: permission?.enabled ? (
          <ActionButton rowData={row} />
        ) : null,
      })),
    [tableData],
  );

  if (!isFilterReady || (isLoading && tableData.length === 0)) {
    return <TableSkeletonLoader rows={10} />;
  }

  return (
    <>
      {selectedRowIndex !== undefined && (
        <PoliciesSidepanel
          bridgePosition={bridgePosition}
          policyData={tableData[selectedRowIndex]}
          isOpen={isSidepanelOpen}
          closeSidepanel={() => {
            setSelectedRowIndex(undefined);
            setBridgePosition({ top: 0, left: 0, height: 0 });
            setIsSidepanelOpen(false);
          }}
          variant="policies"
        />
      )}
      {tableData.length > 0 ? (
        <>
          <Typography
            kind="body3"
            desktop="body3-d"
            mobile="body3-d"
            color="--color-foreground-neutral-subtle"
            className="mb-4"
          >
            {intl.formatMessage(
              { id: "ttMzf4", defaultMessage: "1-{count} of {total}" },
              { count: tableData.length, total: data?.pages[0]?.totalCount },
            )}
          </Typography>
          <TableSortable
            columnConfigs={{ isDndDisabled: true }}
            columns={columns}
            setColumns={setColumns}
            data={tableRows}
            sortConfigs={getSortConfigs(intl)}
            onSort={handleColumnSort}
            onScroll={handleTableScroll}
            onRowClick={handleRowClick}
            selectedRowIndex={selectedRowIndex}
            isWindowScroll={false}
            isVirtualizationEnabled={false}
            tableHeight="min(72vh, calc(100dvh - 14rem))"
            className={classNames("PoliciesPageList-Table", {
              "PoliciesPageList-Table--shrinked": isSidepanelOpen,
              "PoliciesPageList-Table--expanded": !isSidepanelOpen,
            })}
            dataTestId="PoliciesPageList-Table"
          />
        </>
      ) : (
        <div
          className="flex flex-col items-center justify-center"
          style={{ minHeight: "min(72vh, calc(100dvh - 14rem))" }}
        >
          <EmptyState />
        </div>
      )}
      {isFetchingNextPage && (
        <div data-testid="loader-loop">
          <Loader kind="loop" format="main" color="brand" />
        </div>
      )}
    </>
  );
}
